import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import StairsIcon from '@mui/icons-material/Stairs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useListingHeaderLogic } from '../hooks/useListingHeaderLogic';
import { Buttons } from '../../../../constants/Buttons';
import { FeatureFlags } from '../../../../flags';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { PropertyField } from '../../../../system/field';
import { useModal } from '../../../../contexts/ModalContext';
import { useHeaderObserver } from '../hooks/useHeaderObserver';
import { useDebugPreferences } from '../../../../hooks/useDebugPreferences';
import { snakeCase } from 'lodash';

interface DesktopHeaderProps {
  logic: ReturnType<typeof useListingHeaderLogic>;
  handleGroupClick: (
    groupName: string,
    fieldKey?: string,
    isMobile?: boolean,
  ) => void;
}

const DesktopHeader: React.FC<DesktopHeaderProps> = ({
  logic,
  handleGroupClick,
}) => {
  const { hasFeature } = useFeatureFlags();
  const { showModal } = useModal();
  const { ref: headerRef } = useHeaderObserver();
  const {
    isDebugFlagEnabled,
    isDebugSaveEnabled,
    handleDebugSaveChange,
    debugPublish,
    debugSaves,
    handleDebugPublishChange,
    handleDebugSavesChange,
  } = useDebugPreferences();
  const {
    hasErrors,
    anchorEl,
    autosaved,
    autosaveStatus,
    autosaveError,
    saveDraftStatus,
    saveDraftError,
    address,
    formattedStatus,
    formattedPrice,
    formattedExpirationDate,
    beds,
    baths,
    stories,
    listPicture,
    isReturnToLegacy,
    hasReturnToLegacy,
    isAbleToPublish,
    isViewOnly,
    handleReturnToLegacy,
    handleActionsClick,
    handleActionsClose,
    handleSaveDraft,
    handlePreviewDraft,
    handleShareDraft,
    handlePublish,
    handleNavigateBack,
    propertyType,
    latitude,
    longitude,
    isResidental,
    isMediaPending,
  } = logic;

  const showPublishActions = hasFeature(FeatureFlags.NGW_PUBLISH);
  const showPublishOverride = hasFeature(FeatureFlags.NGW_PUBLISH_OVERRIDE);
  const disablePublishButton = !isAbleToPublish();

  const handlePublishOverride = () => {
    if (hasErrors) {
      showModal({
        title: 'Admin Publish Override',
        content:
          'As a NextGenWeb admin you may proceed to publish this listing even though there are form errors. Would you like to proceed?',
        confirmText: 'Proceed',
        cancelText: 'Cancel',
        onConfirm: handlePublish,
      });
    } else {
      handlePublish();
    }
  };

  return (
    <Box
      ref={headerRef}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 2,
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderColor: 'grey.300',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton sx={{ mr: 2 }} onClick={() => handleNavigateBack()}>
          <ArrowBackIcon sx={{ color: 'primary.main' }} />
        </IconButton>
        <Box>
          <Avatar
            sx={{ width: '89px', height: '89px', mr: '15px', borderRadius: 2 }}
            variant="square"
            {...(listPicture
              ? { src: listPicture }
              : {
                  src: `/property_type/${snakeCase(propertyType)}.svg`,
                  imgProps: { style: { objectFit: 'contain' } },
                })}
          ></Avatar>
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontSize: '26px' }}>
            {address}
          </Typography>
          <Box display="flex" gap={1}>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
              fontWeight={700}
            >
              Latitude:
            </Typography>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              {latitude}
            </Typography>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
              fontWeight={700}
            >
              Longitude:
            </Typography>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              {longitude}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 0 }}>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              <BedIcon fontSize="small" /> {beds}
            </Typography>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              <BathtubIcon fontSize="small" /> {baths}
            </Typography>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              <StairsIcon fontSize="small" /> {stories}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flex: 1,
                gap: 2,
              }}
            >
              <Typography variant="body2">
                Price:{' '}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: 'secondary.medium',
                  }}
                  onClick={() =>
                    handleGroupClick(
                      'Listing Details', // where can I get this from?
                      PropertyField.LIST_PRICE,
                    )
                  }
                >
                  {formattedPrice}
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Expires:{' '}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: 'secondary.medium',
                  }}
                  onClick={() =>
                    handleGroupClick(
                      'Listing Agreement', // where can I get this from?
                      PropertyField.EXPIRATION_DATE,
                    )
                  }
                >
                  {formattedExpirationDate}
                </Typography>
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
                color: 'secondary.medium',
              }}
              onClick={() =>
                handleGroupClick(
                  'Listing Details', // where can I get this from?
                  PropertyField.MLS_STATUS,
                )
              }
            >
              {formattedStatus}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            mt: 2.5,
          }}
        >
          {hasReturnToLegacy && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginRight: {
                  md: '0', // Original margin on desktop
                  lg: showPublishActions ? '50px' : '20px',
                },
              }}
            >
              <Switch
                checked={isReturnToLegacy}
                onChange={(event) => handleReturnToLegacy()}
              />
              <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                Return to legacy
              </Typography>
            </Box>
          )}

          {/* Debug Save Checkboxes - only show if ngw_debug flag enabled */}
          {isDebugFlagEnabled && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mr: 2,
                borderLeft: 1,
                borderColor: 'grey.300',
                pl: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDebugSaveEnabled}
                    onChange={handleDebugSaveChange}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    Debug Save
                  </Typography>
                }
              />
              <Collapse in={isDebugSaveEnabled}>
                <Box sx={{ ml: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={debugPublish}
                        onChange={handleDebugPublishChange}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                        Debug Publish
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={debugSaves}
                        onChange={handleDebugSavesChange}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                        Debug Saves
                      </Typography>
                    }
                  />
                </Box>
              </Collapse>
            </Box>
          )}

          {showPublishActions ? (
            <>
              <Button
                onClick={handleActionsClick}
                endIcon={<ArrowDropDownIcon />}
                sx={{
                  color: 'primary.main',
                  mr: 2,
                  textTransform: 'none',
                }}
              >
                Listing Actions
                {saveDraftStatus === 'loading' && (
                  <CircularProgress size={20} sx={{ ml: 1 }} />
                )}
                {saveDraftStatus === 'error' && (
                  <Tooltip title={saveDraftError || 'Error saving draft'}>
                    <ErrorIcon sx={{ color: 'error.main', ml: 1 }} />
                  </Tooltip>
                )}
                {saveDraftStatus === 'success' && (
                  <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                )}
              </Button>

              <Tooltip
                title={
                  isMediaPending
                    ? 'Media uploads or processing in progress'
                    : ''
                }
                disableHoverListener={!isMediaPending}
              >
                <span>
                  <Button
                    variant="contained"
                    sx={Buttons.primary}
                    disabled={
                      hasErrors || disablePublishButton || isMediaPending
                    }
                    onClick={handlePublish}
                  >
                    Publish
                  </Button>
                </span>
              </Tooltip>
              {showPublishOverride && (
                <Button
                  variant="contained"
                  sx={{ ...Buttons.primary, ml: 2 }}
                  disabled={disablePublishButton || isMediaPending}
                  onClick={handlePublishOverride}
                >
                  Publish Override
                </Button>
              )}
            </>
          ) : (
            !isViewOnly() && (
              <Button
                variant="contained"
                disabled={saveDraftStatus === 'loading'}
                sx={Buttons.primary}
                onClick={handleSaveDraft}
              >
                {saveDraftStatus === 'loading' && (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                )}
                Save Draft
              </Button>
            )
          )}
        </Box>

        {autosaved && (
          <Box
            mt={2}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end', // Right align the content
              paddingRight: '16px', // Match the padding of the button container
            }}
          >
            <Typography
              variant="body2"
              component="div"
              color={autosaveStatus === 'failed' ? 'error' : 'text.secondary'}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {autosaveStatus === 'success' || autosaveStatus === 'loading' ? (
                <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
              ) : (
                <Tooltip title={autosaveError || 'Error autosaving'}>
                  <ErrorIcon sx={{ mr: 1 }} />
                </Tooltip>
              )}
              Autosave{autosaveStatus === 'success' ? 'd' : ''}{' '}
              {autosaveStatus === 'success' ? '' : 'failed'} at {autosaved}
            </Typography>
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionsClose}
      >
        {!isViewOnly() && (
          <MenuItem onClick={handleSaveDraft}>Save Draft</MenuItem>
        )}
        {isResidental && hasFeature(FeatureFlags.NGW_PREVIEW_DRAFT) && (
          <MenuItem onClick={handlePreviewDraft}>Preview Draft</MenuItem>
        )}
        {isResidental && hasFeature(FeatureFlags.NGW_SHARE_DRAFT) && (
          <MenuItem onClick={handleShareDraft}>Share Draft</MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default DesktopHeader;
